import React, { useEffect, useRef, useState } from "react";
import { onCapabilityRequired } from "../services/ApiService";
import { ACCESS_ERRORS } from "../utility/errorCatalog";
import { DismissButton, NoticeCard, NoticeDock, NoticeText } from "./AccessGate.styles";

export const ACCESS_DENIED_MESSAGE = "You don't have access to do that.";

/**
 * Says so when the server refuses a call for a capability the user does not have
 * (`403 CAPABILITY_REQUIRED`, through `onCapabilityRequired`). Mounted once in `App.tsx`,
 * beside `ErrorDialogHost`.
 *
 * - It never navigates and never signs anyone out: a refusal is an answer about one action,
 *   not a sign that the session is bad.
 * - It blocks nothing. It sits bottom left, takes no focus and has a Dismiss button.
 * - There is one notice. A refusal while it is showing changes nothing, so a burst of refused
 *   calls is one notice, announced once.
 * - The live region is always in the page and only its text changes, which is what makes a
 *   screen reader announce it (politely).
 */
const AccessDeniedNotice = () => {
  const [shown, setShown] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  // Where focus was before it entered the notice, so Dismiss can give it back.
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => onCapabilityRequired(() => setShown(true)), []);

  const onFocus = (event: React.FocusEvent) => {
    const from = event.relatedTarget as HTMLElement | null;
    if (from && !cardRef.current?.contains(from)) returnFocusRef.current = from;
  };

  const dismiss = () => {
    setShown(false);
    const back = returnFocusRef.current;
    returnFocusRef.current = null;
    if (back && document.contains(back)) back.focus();
  };

  return (
    <NoticeDock>
      {/* The card and its live region are always in the page; only what is in them changes.
          A region added together with its text is often not announced. */}
      <NoticeCard
        ref={cardRef}
        onFocus={onFocus}
        data-shown={shown}
        data-testid="access-denied-notice"
      >
        <NoticeText role="status" aria-live="polite" aria-atomic="true">
          {shown ? (
            <>
              <p>{ACCESS_DENIED_MESSAGE}</p>
              <p>Ask a manager at your site if you need it. ({ACCESS_ERRORS.DENIED.code})</p>
            </>
          ) : null}
        </NoticeText>
        {shown ? (
          <DismissButton type="button" onClick={dismiss}>
            Dismiss
          </DismissButton>
        ) : null}
      </NoticeCard>
    </NoticeDock>
  );
};

export default AccessDeniedNotice;
